import { Context } from "@nuxt/types";

import Api from "@/utils/api";
import debounce from "@/utils/debounce";

interface GetWishesParams {
  id?: string;
  productId?: string;
  ltkProductIds?: string[];
  wishId?: string;
  limit?: number;
  cursor?: string | number;
}

interface GetCollectionsParams {
  id?: string;
}

interface GetCollectionWishesParams extends GetCollectionsParams {
  limit?: number;
  cursor?: string;
}

const DEFAULT_WISHLIST_LIMIT = 36;

export default class WishlistsApi extends Api {
  readonly url = "/v2";

  constructor($axios: Context["$axios"]) {
    super("contentFavoritesService", $axios);
  }

  async getWishes({ id, ltkProductIds }: GetWishesParams) {
    const wishId = id ?? "";
    return await this.request<ManyWishesResponse>({
      method: "GET",
      url: `${this.url}/wishes/${wishId}`,
      params: { ltk_product_ids: ltkProductIds },
    });
  }

  async getMyWishes({ cursor, limit = DEFAULT_WISHLIST_LIMIT }: GetCollectionWishesParams) {
    return await this.request<ManyWishesResponse>({
      method: "GET",
      url: `${this.url}/wishes/`,
      params: { cursor, limit },
    });
  }

  async addWish({ ltkProductId }: { ltkProductId: string }) {
    return await this.request<OneWishResponse>({
      method: "POST",
      url: `${this.url}/wishes/`,
      data: {
        ltk_product_id: ltkProductId,
      },
    });
  }

  async removeWish(id: string) {
    return await this.request<void>({
      method: "DELETE",
      url: `${this.url}/wishes/${id}`,
    });
  }

  async getCollections({ id }: GetCollectionsParams) {
    const collectionId = id ?? "";
    return await this.request<OneCollectionResponse>({
      method: "GET",
      url: `${this.url}/wish_collections/${collectionId}`,
      params: {},
    });
  }

  async getCollectionById({ id }: GetCollectionsParams) {
    const collectionId = id ?? "";
    return await this.request<OneCollectionResponse>({
      method: "GET",
      url: `${this.url}/wish_collections/${collectionId}`,
      params: {},
    });
  }

  async getCollectionWishes({ id, cursor, limit = DEFAULT_WISHLIST_LIMIT }: GetCollectionWishesParams) {
    const collectionId = id ?? "";
    return await this.request<ManyWishesResponse>({
      method: "GET",
      url: `${this.url}/wish_collections/${collectionId}/wishes`,
      params: { cursor, limit },
    });
  }

  @debounce({ timeout: 50, aggregate: true, batch: 10 })
  async getOneWishByLtkProductId(...ltkProductIds: string[]) {
    return await this.getWishes({ ltkProductIds });
  }
}
